import { useMemo } from "react";
import { useExperiments } from "./useExperiments";
import { useUser } from "./useUser";
import { WorkspacePlanTier } from "common/interfaces";

export type CheckoutVariant = "A" | "B";

export interface CheckoutVariantResult {
    containerVariant: CheckoutVariant;
    contentVariant: CheckoutVariant;
    loading: boolean;
}

/**
 * Hook to determine checkout container and content variants based on URL params, user subscription status, and experiments.
 *
 * Priority order:
 * 1. URL param `variant` (takes precedence, expects "A" or "B")
 * 2. Existing Pro/Team subscription → A container, A content (always show old checkout UI)
 * 3. Experiment combinations:
 *    - ai_onboarding_flow: ON  + checkout_pro_variant: ON  → B container, B content
 *    - ai_onboarding_flow: ON  + checkout_pro_variant: OFF → B container, A content
 *    - ai_onboarding_flow: OFF + checkout_pro_variant: ON  → B container, B content
 *    - ai_onboarding_flow: OFF + checkout_pro_variant: OFF → A container, A content
 *
 * @returns Object with containerVariant, contentVariant, and loading state
 */
export function useCheckoutVariant(): CheckoutVariantResult {
    const {
        loading: experimentsLoading,
        experiments: {
            ai_onboarding_flow: isAiOnboardingFlow,
            checkout_pro_variant: isCheckoutProVariant
        }
    } = useExperiments(["ai_onboarding_flow", "checkout_pro_variant"]);

    const { user, loading: userLoading } = useUser();

    const variants = useMemo<{ containerVariant: CheckoutVariant, contentVariant: CheckoutVariant }>(() => {
        // Check URL params first - takes precedence
        const urlParams = new URLSearchParams(window.location.search);
        const urlVariant = urlParams.get("variant") as CheckoutVariant | null;

        if (urlVariant === "A" || urlVariant === "B") {
            return {
                containerVariant: urlVariant,
                contentVariant: urlVariant
            };
        }

        // Check if user has existing Pro or Team subscription
        if (user) {
            const userWorkspaces = user.workspacesMetadata || [];
            const hasProOrTeamSubscription = userWorkspaces.some(metadata =>
                metadata.plan.tier === WorkspacePlanTier.PRO || metadata.plan.tier === WorkspacePlanTier.TEAM
            );

            if (hasProOrTeamSubscription) {
                return {
                    containerVariant: "A",
                    contentVariant: "A"
                };
            }
        }

        // Determine variants based on experiment combinations
        let containerVariant: CheckoutVariant;
        let contentVariant: CheckoutVariant;

        if (isAiOnboardingFlow) {
            // ai_onboarding_flow: ON
            if (isCheckoutProVariant) {
                // checkout_pro_variant: ON → B for both
                containerVariant = "B";
                contentVariant = "B";
            } else {
                // checkout_pro_variant: OFF → B for container, A for content
                containerVariant = "B";
                contentVariant = "A";
            }
        } else {
            // ai_onboarding_flow: OFF
            if (isCheckoutProVariant) {
                // checkout_pro_variant: ON → B for both
                containerVariant = "B";
                contentVariant = "B";
            } else {
                // checkout_pro_variant: OFF → A for both
                containerVariant = "A";
                contentVariant = "A";
            }
        }

        return { containerVariant, contentVariant };
    }, [isAiOnboardingFlow, isCheckoutProVariant, user, userLoading]);

    return {
        ...variants,
        loading: experimentsLoading || userLoading
    };
}
